import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { useTranslation } from 'react-i18next';
import Box from '@mui/material/Box';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { Button, Loader } from '@repo/ui';
import { useGetMerchant } from '@repo/api/endpoints/merchant';
import { useMainContext } from '@repo/app-contexts/main';
import { useProviderStore } from '@repo/store/provider';
import { useIsMobile } from '@repo/mui-config/hooks';
import { useApiErrorStoreActions } from '@repo/api/client';
import { useEventClick } from '@repo/hooks';

import { LoanProcessStatusIconWrapper } from '@/components/loan-process/loan-process-status-icon-wrapper';
import { useLoanProcessSetCurrentModalState } from '@/components/loan-process/hooks/use-loan-process-set-current-modal-state';
import { useMainHubStoreActions, useMainHubStoreState } from '@/store/main-hub.store';
import { useLoanProcessStore } from '@/components/loan-process/store/loan-process.store';
import { LoanProcessModalHeader } from '@/components/loan-process/loan-process-modal/loan-process-modal-header';
import { setCurrentViewForMerchantHashResponse } from '@/store/set-current-view-for-merchant-hash-response';
import { getViewEntryEventPayload } from '@/store/get-view-entry-event-payload';

export function ApplicationSuspended() {
  const isMobile = useIsMobile();
  useLoanProcessSetCurrentModalState('ApplicationSuspended');
  const { getApiErrorCode } = useApiErrorStoreActions();
  const apiErrorCode = getApiErrorCode();
  const { t } = useTranslation();
  const {
    actions: { clearLoanProcessStore },
  } = useLoanProcessStore();
  const { hash, token } = useMainContext();
  const { refetch, isFetching } = useGetMerchant({
    hash,
    token,
    enabled: false,
    skipDelay: true,
  });
  const { setAppState } = useMainHubStoreActions();
  const { merchantResponseData: sessionMerchantResponseData, currentViewType } =
    useMainHubStoreState();
  const trackClick = useEventClick(sessionMerchantResponseData);
  const {
    state: { currentProvider },
  } = useProviderStore();

  const closeModalAndRedirect = () => {
    const viewBeforeRedirect = currentViewType;
    void refetch().then((merchantResponseData) => {
      const redirectPath = setCurrentViewForMerchantHashResponse({
        merchantResponseData,
        currentProvider,
      });
      clearLoanProcessStore();
      setAppState(redirectPath, merchantResponseData);
      if (redirectPath === viewBeforeRedirect) {
        const entryEvent = getViewEntryEventPayload(redirectPath, sessionMerchantResponseData);
        if (entryEvent) {
          trackClick(entryEvent);
        }
      }
    });
  };

  return (
    <Box data-testid="application-suspended" sx={{ px: isMobile ? 2 : 4, py: 4 }}>
      <LoanProcessModalHeader containerSx={{ mb: isMobile ? 4 : 8 }} />
      <Stack alignContent="center" alignItems="center" gap="56px">
        {isFetching ? (
          <Loader size={80} />
        ) : (
          <>
            <Typography component="p" textAlign="center" variant="h4">
              {t('loanProcess.loanSteps.application.suspended.title')}
            </Typography>
            <Box>
              <LoanProcessStatusIconWrapper iconType="info">
                <InfoOutlinedIcon />
              </LoanProcessStatusIconWrapper>
            </Box>
            <Typography textAlign="center">
              {t(
                'loanProcess.loanSteps.application.suspended.description.sorryTechnicalErrorHasOccured',
              )}
              <br />
              {t('loanProcess.loanSteps.application.suspended.description.tryAgainLater')}
              <br />
              {apiErrorCode ? (
                <>
                  {t('loanProcess.loanSteps.application.suspended.errorCode')} {apiErrorCode}
                </>
              ) : null}
            </Typography>
          </>
        )}
        <Button
          color="secondary"
          data-testid="application-suspended-button"
          fullWidth
          onClick={closeModalAndRedirect}
          sx={{ paddingY: { md: '14px', xs: '8px' }, marginTop: '37px' }}
          variant="contained"
          disabled={isFetching}
        >
          {t('loanProcess.loanSteps.application.suspended.button')}
        </Button>
      </Stack>
    </Box>
  );
}
